import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutline';
import { approveFeeChangeRequest, listFeeChangeRequests, rejectFeeChangeRequest } from '../../api/studentFees';
import { FeeChangeRequest, FeeChangeStatus } from '../../types/studentFees';
import { PageHeader } from '../../components/PageHeader';

const extractErrorMessage = (err: unknown): string =>
  isAxiosError(err) ? (err.response?.data?.message ?? 'Something went wrong') : 'Something went wrong';

const STATUS_COLOR: Record<FeeChangeStatus, 'warning' | 'success' | 'error'> = {
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'error',
};

/**
 * Super Admin's (or another fee_changes:approve holder's) approval queue
 * (§12/§41) — mirrors LeadApprovalsPage's own pattern: reuses the same
 * listing every request already comes from, filtered by status, rather
 * than a parallel data source.
 */
export const FeeChangeApprovalsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<FeeChangeStatus | ''>('PENDING');
  const [rejectTarget, setRejectTarget] = useState<FeeChangeRequest | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['fee-change-requests', status],
    queryFn: () => listFeeChangeRequests({ page: 1, pageSize: 50, status: status || undefined }),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['fee-change-requests'] });

  const approveMutation = useMutation({
    mutationFn: (id: number) => approveFeeChangeRequest(id),
    onSuccess: invalidate,
    onError: (err) => setError(extractErrorMessage(err)),
  });

  const rejectMutation = useMutation({
    mutationFn: () => rejectFeeChangeRequest(rejectTarget!.id, rejectReason),
    onSuccess: () => {
      invalidate();
      setRejectTarget(null);
      setRejectReason('');
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  const requests = query.data?.data ?? [];

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={RequestQuoteOutlinedIcon}
        color="#B5451B"
        title="Fee Change Approvals"
        subtitle="Review student-specific fee amount changes before they take effect."
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TextField
        select
        size="small"
        label="Status"
        sx={{ width: 200, mb: 2 }}
        value={status}
        onChange={(e) => setStatus(e.target.value as FeeChangeStatus | '')}
      >
        <MenuItem value="">
          <em>All</em>
        </MenuItem>
        <MenuItem value="PENDING">Pending Approval</MenuItem>
        <MenuItem value="APPROVED">Approved</MenuItem>
        <MenuItem value="REJECTED">Rejected</MenuItem>
      </TextField>

      {requests.length === 0 ? (
        <Paper variant="outlined" sx={{ py: 6, textAlign: 'center' }}>
          <CheckCircleOutlinedIcon sx={{ fontSize: 48, color: 'success.main', mb: 1.5 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Nothing to review
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No fee change requests match this filter.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {requests.map((request) => (
            <Paper key={request.id} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/students/${request.student.id}`)}
                  >
                    {request.requestNumber} — {request.student.firstName} {request.student.lastName} (
                    {request.student.studentNumber})
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Requested by {request.requestedBy.firstName} {request.requestedBy.lastName} on{' '}
                    {new Date(request.requestedAt).toLocaleString()}
                  </Typography>
                </Box>
                <Chip size="small" label={request.status} color={STATUS_COLOR[request.status]} />
              </Stack>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fee</TableCell>
                    <TableCell align="right">Default/Current</TableCell>
                    <TableCell align="right">New</TableCell>
                    <TableCell align="right">Difference</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {request.items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.paymentCategory.name}</TableCell>
                      <TableCell align="right">
                        {item.currency.symbol ?? item.currency.code} {Number(item.previousAmount).toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        {item.currency.symbol ?? item.currency.code} {Number(item.newAmount).toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        {(Number(item.newAmount) - Number(item.previousAmount)).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Typography variant="body2" sx={{ mt: 1.5 }}>
                <strong>Reason:</strong> {request.reason}
              </Typography>

              {request.status !== 'PENDING' && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {request.status === 'APPROVED' ? 'Approved' : 'Rejected'} by {request.decidedBy?.firstName}{' '}
                  {request.decidedBy?.lastName} on{' '}
                  {request.decidedAt ? new Date(request.decidedAt).toLocaleString() : ''}
                  {request.decisionReason ? ` — ${request.decisionReason}` : ''}
                </Typography>
              )}

              {request.status === 'PENDING' && (
                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    disabled={approveMutation.isPending}
                    onClick={() => approveMutation.mutate(request.id)}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => {
                      setRejectTarget(request);
                      setRejectReason('');
                    }}
                  >
                    Reject
                  </Button>
                </Stack>
              )}
            </Paper>
          ))}
        </Stack>
      )}

      <Dialog open={!!rejectTarget} onClose={() => setRejectTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reject fee change request</DialogTitle>
        <DialogContent>
          <TextField
            label="Reason"
            required
            multiline
            minRows={2}
            fullWidth
            sx={{ mt: 1 }}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={!rejectReason.trim() || rejectMutation.isPending}
            onClick={() => rejectMutation.mutate()}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
